import React from 'react';
import cn from 'classnames';

import { Tooltip } from 'components/Tooltip';

import { MenuTab } from './components/MenuTab';
import { SwitchTab } from './components/SwitchTab';
import { CountTab } from './components/CountTab';
import { DefaultTab } from './components/DefaultTab';
import { PropertiesTab } from './components/PropertiesTab';

import styles from './Tabs.module.scss';

const Tabs = ({
  className,
  innerClassName,
  items,
  actions,
  startActions,
  variant,
  activeTab,
  onTabClick,
  textClassName,
}: {
  className: any;
  innerClassName: any;
  items: any;
  actions: any;
  startActions: any;
  variant: any;
  activeTab: any;
  onTabClick: any;
  textClassName: any;
}) => (
  <div className={cn(styles.tabs, className)}>
    <div
      className={cn(styles.header, {
        [styles.headerSwitch]: variant === 'switch',
      })}
    >
      {startActions.length > 0 && (
        <div className={styles.actions}>
          {startActions.map((action: any) => {
            if (action.withTooltip) {
              return (
                <Tooltip
                  key={action.key}
                  text={action.tooltipText}
                  triggerElement={<action.component key={action.key} />}
                />
              );
            }

            return React.cloneElement(action.component, { key: action.key });
          })}
        </div>
      )}
      <div
        className={cn(styles.items, {
          [styles.itemsMenu]: variant === 'menu',
          [styles.itemsSwitch]: variant === 'switch',
        })}
      >
        {items.filter(Boolean).map((item: any) => {
          const { label, icon, count, disabled } = item;

          switch (variant) {
            case 'count': {
              return (
                <CountTab
                  key={label}
                  label={label}
                  icon={icon}
                  count={count}
                  active={activeTab === label}
                  disabled={disabled}
                  onClick={onTabClick}
                />
              );
            }
            case 'menu': {
              return (
                <MenuTab
                  key={label}
                  label={label}
                  icon={icon}
                  active={activeTab === label}
                  disabled={disabled}
                  onClick={onTabClick}
                />
              );
            }
            case 'properties': {
              return (
                <PropertiesTab
                  key={label}
                  label={label}
                  active={activeTab === label}
                  disabled={disabled}
                  onClick={onTabClick}
                />
              );
            }
            case 'switch': {
              return (
                <SwitchTab
                  key={label}
                  label={label}
                  icon={icon}
                  active={activeTab === label}
                  disabled={disabled}
                  onClick={onTabClick}
                />
              );
            }
            default: {
              return (
                <DefaultTab
                  key={label}
                  label={label}
                  active={activeTab === label}
                  disabled={disabled}
                  onClick={onTabClick}
                  textClassName={textClassName}
                />
              );
            }
          }
        })}
      </div>
      {actions.length > 0 && (
        <div className={styles.actions}>
          {actions.map((action: any) => {
            if (action.withTooltip) {
              return (
                <Tooltip
                  key={action.key}
                  text={action.tooltipText}
                  triggerElement={<action.component key={action.key} />}
                />
              );
            }

            return React.cloneElement(action.component, { key: action.key });
          })}
        </div>
      )}
    </div>
    <div
      className={cn(
        styles.inner,
        { [styles.innerMenu]: variant === 'menu' },
        { [styles.innerProperties]: variant === 'properties' },
        innerClassName,
      )}
    >
      {items
        .filter(Boolean)
        .filter((item: any) => item.label === activeTab)
        .map((item: any) => {
          if (!item.content) {
            return null;
          }

          return (
            <React.Fragment key={item.label}>{item.content}</React.Fragment>
          );
        })}
    </div>
  </div>
);

Tabs.defaultProps = {
  startActions: [],
  actions: [],
  variant: 'default',
};

export default React.memo(Tabs) as any;
